import React, { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MachineLogoIcon from "../../../shared/assets/icons/MachineLogoIcon";
import colors from "../../../shared/themes/colors";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../shared/components/Loader";
import { refreshAccount } from "../../accountSlice";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { resetLoginState } from "../../loginSlice";
import useTranslation from "../../../shared/hooks/useTranslation";
import LoginForm from "./LoginForm";
import { isComAppHost } from "../../../shared/utils/hostPolicy";
import useDefaultRedirect from "../../hooks/useDefaultRedirect";

interface LoginDialogProps {
  open: boolean;
  onToRegistration?: () => void;
  onToForgotPassword?: () => void;
  onClose: () => void;
  returnUrl?: string;
}

const LoginDialog = ({ open, onToRegistration, onToForgotPassword, onClose, returnUrl }: LoginDialogProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { translate } = useTranslation("account");
  const { defaultRoute } = useDefaultRedirect();

  const { isLoading } = useAppSelector((state) => state.login);
  const { user } = useAppSelector((state) => state.account);

  const handleLoginSuccess = () => {
    dispatch(refreshAccount(null));
    onClose();
    const search = new URLSearchParams(location.search);
    const fromQuery = search.get("returnUrl");
    const target = returnUrl || fromQuery;
    navigate(target || defaultRoute(), { replace: true });
  };

  useEffect(() => {
    if (open) {
      dispatch(resetLoginState());
    }
  }, [open, dispatch]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        sx: {
          ...(isMobile ? {
            margin: 0,
            borderRadius: "8px",
            maxHeight: "100vh",
            height: "100vh",
            width: "100vw",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "16px",
          } : {
            width: "600px",
            minHeight: "406px",
            display: "flex",
            justifyContent: "center",
            borderRadius: "8px",
            padding: "64px",
          })
        }
      }}
    >
      {(isLoading || user) && (
        <Box
          sx={{
            ...(isMobile ? {
              width: '100%',
              height: '100vh',
            } : {
              width: '460px',
              minHeight: '420px',
            }),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Loader />
        </Box>
      )}

      {!isLoading && !user && (
        <Box
          sx={{
            ...(isMobile && {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            })
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              ...(isMobile && {
                paddingTop: "60px",
                flex: "0 0 auto"
              })
            }}
          >
            <MachineLogoIcon />
            <Box
              className="text-36-medium"
              sx={{
                color: colors.secondary.black,
                ...(isMobile && {
                  fontSize: "32px",
                  marginTop: "16px"
                })
              }}
            >
              {translate("logIn")}
            </Box>
          </DialogTitle>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: isMobile ? 16 : 14,
              top: isMobile ? 16 : 14,
              color: colors.secondary.gray20,
              ...(isMobile && {
                padding: "12px",
                '& .MuiSvgIcon-root': {
                  fontSize: '24px'
                }
              })
            }}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent
            sx={{
              padding: "18px 0 20px",
              ...(isMobile && {
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
              })
            }}
          >
            {/* HOST_POLICY_SPEC §6 FE-B: see LoginPage — sign-up is hidden on `.com` app hosts. */}
            <LoginForm
              formId="loginForm"
              onSubmitSuccess={handleLoginSuccess}
              onForgotPassword={onToForgotPassword}
              onRegistration={onToRegistration}
              showRegistration={!isComAppHost()}
              className={isMobile ? "flex-1 flex flex-col justify-center" : ""}
            />
          </DialogContent>
        </Box>
      )}
    </Dialog>
  );
};

export default LoginDialog;
